import React, { useRef, useState } from 'react';
import { Upload, Image as ImageIcon, Video, X, Plus, Layers, Play } from 'lucide-react';
import { MediaFile } from '../../types';

interface MediaUploadProps {
  mediaFiles: MediaFile[];
  onMediaAdd: (files: MediaFile[]) => void;
  onMediaRemove: (index: number) => void;
  onMediaReorder?: (fromIndex: number, toIndex: number) => void;
  maxFiles?: number;
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
  mediaFiles,
  onMediaAdd,
  onMediaRemove,
  onMediaReorder,
  maxFiles = 10
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newMediaFiles: MediaFile[] = [];
    const filesToProcess = Array.from(files).slice(0, maxFiles - mediaFiles.length);
    
    let processedCount = 0;
    
    filesToProcess.forEach((file, index) => {
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        const url = URL.createObjectURL(file);
        const type = file.type.startsWith('image/') ? 'image' : 'video';
        
        if (type === 'image') {
          const img = new Image();
          img.onload = () => {
            newMediaFiles.push({
              file,
              url,
              type,
              width: img.width,
              height: img.height
            });
            
            processedCount++;
            if (processedCount === filesToProcess.length) {
              // Sort by original file order
              newMediaFiles.sort((a, b) => {
                const aIndex = filesToProcess.findIndex(f => f === a.file);
                const bIndex = filesToProcess.findIndex(f => f === b.file);
                return aIndex - bIndex;
              });
              onMediaAdd(newMediaFiles);
            }
          };
          img.onerror = () => {
            processedCount++;
            if (processedCount === filesToProcess.length && newMediaFiles.length > 0) {
              onMediaAdd(newMediaFiles);
            }
          };
          img.src = url;
        } else {
          // For videos, we can't easily get dimensions without loading
          newMediaFiles.push({ file, url, type });
          processedCount++;
          
          if (processedCount === filesToProcess.length) {
            onMediaAdd(newMediaFiles);
          }
        }
      } else {
        processedCount++;
        if (processedCount === filesToProcess.length && newMediaFiles.length > 0) {
          onMediaAdd(newMediaFiles);
        }
      }
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleMediaDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleMediaDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleMediaDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex !== null && draggedIndex !== dropIndex && onMediaReorder) {
      onMediaReorder(draggedIndex, dropIndex);
    }
    
    setDraggedIndex(null);
  };

  const handleMediaDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {mediaFiles.length < maxFiles && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragOver
              ? 'border-purple-400 bg-purple-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="flex flex-col items-center">
            <Upload className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Upload photos and videos
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Drag and drop your files here, or click to browse
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Supports JPEG, PNG, WebP, GIF, MP4, WebM • Max {maxFiles} files
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors"
            >
              Choose Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </div>
        </div>
      )}

      {/* Media Preview Grid */}
      {mediaFiles.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700 flex items-center">
              <Layers className="h-4 w-4 mr-2" />
              Media Files ({mediaFiles.length}/{maxFiles})
            </h4>
            {mediaFiles.length > 1 && (
              <p className="text-xs text-gray-500">Drag to reorder</p>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {mediaFiles.map((media, index) => (
              <div 
                key={index} 
                className={`relative group cursor-move ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
                draggable={mediaFiles.length > 1}
                onDragStart={(e) => handleMediaDragStart(e, index)}
                onDragOver={(e) => handleMediaDragOver(e, index)}
                onDrop={(e) => handleMediaDrop(e, index)}
                onDragEnd={handleMediaDragEnd}
              >
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent group-hover:border-purple-300 transition-colors">
                  {media.type === 'image' ? (
                    <img
                      src={media.url}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="relative w-full h-full">
                      <video
                        src={media.url}
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-black bg-opacity-50 rounded-full p-2">
                          <Play className="h-6 w-6 text-white fill-white" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Media Type Indicator */}
                <div className="absolute top-2 left-2">
                  {media.type === 'image' ? (
                    <div className="bg-black bg-opacity-50 rounded-full p-1">
                      <ImageIcon className="h-3 w-3 text-white" />
                    </div>
                  ) : (
                    <div className="bg-black bg-opacity-50 rounded-full p-1">
                      <Video className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => onMediaRemove(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title="Remove media"
                >
                  <X className="h-3 w-3" />
                </button>

                {/* Order Indicator */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded font-medium">
                  {index + 1}
                </div>

                {/* File Size */}
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                  {(media.file.size / 1024 / 1024).toFixed(1)}MB
                </div>
              </div>
            ))}

            {/* Add More Button */}
            {mediaFiles.length < maxFiles && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-purple-400 hover:bg-purple-50 transition-colors group"
              >
                <Plus className="h-8 w-8 text-gray-400 group-hover:text-purple-500 mb-2" />
                <span className="text-xs text-gray-500 group-hover:text-purple-600">Add More</span>
              </button>
            )}
          </div>

          {/* Upload Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 className="text-sm font-medium text-blue-900 mb-1">Tips for better posts:</h5>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• First image/video will be the cover</li>
              <li>• Drag to reorder your media files</li>
              <li>• Mix photos and videos for engaging content</li>
              <li>• Keep file sizes under 50MB for faster uploads</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};